import React from "react"
import { Formik, Form } from "formik"
import * as yup from "yup"
import { Link } from "react-router-dom"

import {
  ConnectorDialog,
  ConnectFormField,
} from "~/ui-rtk/components/features/connectors"
import { connectorIcons } from "~/ui-rtk/shared/constants/image-paths.contants"
import { useAirbyteAdditionalConfig } from "~/ui-rtk/hooks/useAirbyteAdditionalConfig"
import useConnectors from "~/ui-rtk/hooks/connectors"
import { Button } from "~/ui-rtk/components/ui/controls"

import type { CustomerConnectorDialogFormPropsWithoutUri } from "../types"

const validationSchema = yup.object().shape({
  appKey: yup.string().required("App Key is required!"),
  secretKey: yup.string().required("Secret Key is required!"),
})

type FormValues = yup.InferType<typeof validationSchema>

const initialValues: FormValues = {
  appKey: "",
  secretKey: "",
}

type YotpoAirbyteFormProps = CustomerConnectorDialogFormPropsWithoutUri

/**
 * Bespoke setup dialog for the yotpo_airbyte connector (legacy yotpo parity):
 * collects App Key + Secret Key and submits them as additionalConfig — the
 * backend exchanges them for the Yotpo access token before creating the
 * Airbyte source, so customers never generate or paste a token by hand.
 */
const YotpoAirbyteForm: React.FC<YotpoAirbyteFormProps> = ({
  companyConnectorId,
  isOpen,
  onClose,
}) => {
  const { getConnectorById } = useConnectors()
  const connector = getConnectorById("yotpo_airbyte")
  const { completeSetup, isCompletingSetup } = useAirbyteAdditionalConfig(
    companyConnectorId ?? "",
    connector,
  )

  const handleConnect = async (values: FormValues) => {
    if (isCompletingSetup) {
      return
    }

    try {
      await completeSetup({
        app_key: values.appKey,
        secret_key: values.secretKey,
      })
      onClose()
    } catch {
      // completeSetup already toasts the backend error (e.g. invalid Yotpo
      // credentials); keep the dialog open so the user can retry.
    }
  }

  if (!connector) {
    return null
  }

  return (
    <ConnectorDialog
      imgSrc={connectorIcons.yotpo}
      alt="Yotpo Logo"
      title="Connection to Yotpo"
      isOpen={isOpen}
      onClose={onClose}
    >
      <p className="leading-5 text-left text-base text-platinum">
        In order to connect to Yotpo, you will be required to provide your App
        Key and Secret Key. They can be found in General Settings (
        <Link
          to="https://settings.yotpo.com/#/general_settings"
          target="_blank"
          className="text-blue"
        >
          https://settings.yotpo.com/#/general_settings
        </Link>
        ) under API Credentials, like below.
      </p>
      <img
        src="/assets/img/yotpo_credentials.png"
        alt="Yotpo General Settings"
      />
      <Formik
        onSubmit={handleConnect}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur={false}
      >
        {({ touched, errors }) => (
          <Form className="flex flex-col w-full gap-4 mt-8">
            <div className="space-y-3">
              <ConnectFormField
                name="appKey"
                label="App Key*"
                placeholder="Enter your App Key here"
                isError={Boolean(touched.appKey && errors.appKey)}
              />
              <ConnectFormField
                name="secretKey"
                label="Secret Key*"
                placeholder="Enter your Secret Key here"
                isError={Boolean(touched.secretKey && errors.secretKey)}
              />
            </div>

            <Button
              variant={{ variant: "solid", color: "white" }}
              type="submit"
              isLoading={isCompletingSetup}
            >
              Connect
            </Button>
          </Form>
        )}
      </Formik>
    </ConnectorDialog>
  )
}

export default YotpoAirbyteForm
